import {
  createSecureHeaders,
  obfuscatePayload,
  deobfuscatePayload,
} from "./api-security";

/** Thrown when the server returns a 401 Unauthorized response. */
export class AuthExpiredError extends Error {
  constructor(message = "Your session has expired. Please sign in again.") {
    super(message);
    this.name = "AuthExpiredError";
  }
}

type TokenProvider = () => Promise<string | null>;
type ApiFetchOptions = RequestInit & { cacheAuth?: boolean };

type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

/** Public endpoints that don't require security headers/obfuscation */
const INSECURE_PATHS = ["/featured-events"];

/**
 * Sub-paths under /events/:id/ that require security headers (@Secure on backend).
 * Everything else under /events/ is treated as insecure (public reads).
 */
const SECURE_EVENT_SUBPATHS = [
  "/checkout",
  "/rsvp/cancel",
  "/invoice",
  "/invites",
  "/vouchers/checkout",
  "/cover-charge/topup",
  "/save",  // POST toggle-save has @Secure; GET check-save does not but sending headers is harmless
];
const NO_CLIENT_CACHE_PATHS = ["/cities", "/admin/cities"];

const DEFAULT_CACHE_TTL_MS = 30_000;
const responseCache = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

/** Whether we're running in the browser (safe to use in-memory cache) */
const isClient = typeof window !== "undefined";

/** Read the active org ID from localStorage (SSR-safe). */
const getActiveOrgId = (): string | null =>
  isClient ? localStorage.getItem("activeOrgId") : null;

/**
 * Environment prefix for cache keys to prevent cross-environment contamination.
 * Staging and production would otherwise share identical cache keys like `GET:/api/proxy/events/123`.
 * On the server side, caching is skipped entirely to avoid stale data across SSR requests.
 */
const envPrefix: string = process.env.NEXT_PUBLIC_API_URL
  ? new URL(process.env.NEXT_PUBLIC_API_URL).host
  : isClient
    ? window.location.origin
    : "ssr";

const normalizeApiBaseUrl = (baseUrl: string) => {
  const trimmed = baseUrl.trim().replace(/\/+$/, "");
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `http://${trimmed}`;
};

const normalizeApiPath = (path: string) => {
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  return withSlash.split("?")[0];
};

const shouldBypassClientCache = (path: string) => {
  const normalized = normalizeApiPath(path);
  return NO_CLIENT_CACHE_PATHS.some(
    (base) =>
      normalized === base ||
      normalized.startsWith(`${base}/`) ||
      normalized.startsWith(`${base}?`),
  );
};

const getCachedValue = <T>(key: string) => {
  const entry = responseCache.get(key);
  if (!entry) {
    return null;
  }
  if (Date.now() > entry.expiresAt) {
    responseCache.delete(key);
    return null;
  }
  return entry.value as T;
};

const setCachedValue = <T>(key: string, value: T, ttlMs: number) => {
  responseCache.set(key, { value, expiresAt: Date.now() + ttlMs });
};

const stringifyUnknown = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => stringifyUnknown(item)).filter(Boolean).join("; ");
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (typeof obj.message === "string") {
      return obj.message;
    }
    return JSON.stringify(value);
  }
  return "";
};

const extractErrorMessage = (payload: unknown, fallback: string): string => {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const obj = payload as Record<string, unknown>;
  const messageValue = obj.message;
  const detailsValue = obj.details;

  if (Array.isArray(messageValue) && messageValue.length > 0) {
    return messageValue.map((item) => stringifyUnknown(item)).filter(Boolean).join("; ");
  }

  if (typeof messageValue === "string" && messageValue.trim().length > 0) {
    if (typeof detailsValue === "string" && detailsValue.trim().length > 0) {
      return `${messageValue}: ${detailsValue}`;
    }
    return messageValue;
  }

  if (typeof detailsValue === "string" && detailsValue.trim().length > 0) {
    return detailsValue;
  }

  return fallback;
};

/**
 * Returns the absolute app URL for SSR proxy calls.
 * Uses NEXT_PUBLIC_APP_URL so SSR requests also go through the Next.js proxy,
 * ensuring a single code path for auth injection and request handling.
 */
const getAppBaseUrl = () => {
  return process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3001";
};

/**
 * Build the API URL. ALL requests (client + SSR) go through the Next.js proxy.
 * - Client-side: relative URL `/api/proxy/...`
 * - SSR: absolute URL `${APP_URL}/api/proxy/...`
 *
 * This ensures ONE code path for auth, security headers, and error handling.
 * No direct backend calls from the frontend.
 */
export const buildApiUrl = (path: string) => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (typeof window !== "undefined") {
    return `/api/proxy${normalized}`;
  }
  // SSR: absolute URL through the same proxy
  return `${getAppBaseUrl()}/api/proxy${normalized}`;
};

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
  token?: string | null,
): Promise<T> {
  const { cacheAuth, ...fetchOptions } = options;
  const method = (fetchOptions.method ?? "GET").toUpperCase();
  const headers = new Headers(fetchOptions.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && fetchOptions.body) {
    headers.set("Content-Type", "application/json");
  }
  const activeOrgId = getActiveOrgId();
  if (activeOrgId) {
    headers.set("X-Active-Org-Id", activeOrgId);
  }

  const url = buildApiUrl(path);
  const hasAuth = headers.has("Authorization");
  const cacheMode = fetchOptions.cache;
  const canCache =
    isClient &&
    method === "GET" &&
    !fetchOptions.body &&
    !shouldBypassClientCache(path) &&
    cacheMode !== "no-store" &&
    (!hasAuth || cacheAuth);
  const cacheKey = `${envPrefix}:${method}:${url}`;

  if (canCache) {
    const cached = getCachedValue<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }
    const pending = inFlight.get(cacheKey);
    if (pending) {
      return (await pending) as T;
    }
  }

  const requestPromise = (async () => {
    const fetchController = new AbortController();
    const fetchTimeout = setTimeout(() => fetchController.abort(), 40000);

    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: fetchOptions.signal ?? fetchController.signal,
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      throw err;
    } finally {
      clearTimeout(fetchTimeout);
    }

    if (!response.ok) {
      const text = await response.text();
      const fallback = `Request failed (${response.status})`;
      let message = text || fallback;

      // Try to parse JSON error response and extract the message
      try {
        const parsed = JSON.parse(text);
        message = extractErrorMessage(parsed, fallback);
      } catch {
        // Not JSON — likely an HTML error page (e.g. Cloudflare 502).
        // Never show raw HTML to the user.
        if (text.includes("<!DOCTYPE") || text.includes("<html")) {
          message = response.status === 502 || response.status === 503
            ? "Server is temporarily unavailable. Please try again in a moment."
            : fallback;
        }
      }

      if (response.status === 401) {
        throw new AuthExpiredError();
      }

      throw new Error(message);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  })();

  if (canCache) {
    inFlight.set(cacheKey, requestPromise as Promise<unknown>);
  }

  try {
    const data = await requestPromise;
    if (canCache) {
      setCachedValue(cacheKey, data, DEFAULT_CACHE_TTL_MS);
    }
    return data;
  } finally {
    if (canCache) {
      inFlight.delete(cacheKey);
    }
  }
}

/**
 * Check if a path is considered insecure (public endpoint).
 *
 * /events is special: the listing and most read-only sub-paths are public,
 * but several mutation endpoints under /events/:id/ have @Secure() on the
 * backend and therefore need security headers from the frontend.
 */
const isInsecurePath = (path: string): boolean => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const pathOnly = normalizedPath.split("?")[0];

  // Exact insecure paths (/waitlist, /featured-events)
  if (
    INSECURE_PATHS.some(
      (p) => pathOnly === p || pathOnly.startsWith(`${p}/`) || pathOnly.startsWith(`${p}?`),
    )
  ) {
    return true;
  }

  // /events requires special handling: most reads are public but several
  // mutation/payment sub-paths have @Secure() on the backend.
  if (pathOnly === "/events") return true;
  if (pathOnly.startsWith("/events/")) {
    // If the path contains any secure sub-path keyword, it needs security headers
    if (SECURE_EVENT_SUBPATHS.some((sub) => pathOnly.includes(sub))) {
      return false;
    }
    // Everything else under /events/ is public (detail, rsvp-status, posts, etc.)
    return true;
  }

  return false;
};

/**
 * Try deobfuscation with explicit key components first (from response headers),
 * then fall back to browser-derived key. This handles:
 * - Normal case: backend sends x-xor-ua / x-xor-cd headers → exact key match
 * - Proxy strips headers: fall back to browser properties (should still match)
 * - Deployment transition: old backend without new headers → browser key fallback
 */
function tryDeobfuscate<T>(
  text: string,
  xorUa: string | null,
  xorCd: string | null,
): T {
  // Try with explicit key components from backend (most reliable)
  if (xorUa && xorCd) {
    try {
      return deobfuscatePayload(text, xorUa, xorCd) as T;
    } catch {
      // Fall through to browser key
    }
  }
  // Fall back to browser-derived key
  return deobfuscatePayload(text) as T;
}

/**
 * Secure version of apiFetch that adds security headers and body obfuscation.
 * - Generates and adds security headers (signature, timestamp, nonce, fingerprint)
 * - Obfuscates request body
 * - Deobfuscates response body
 * - Adds `x-obfuscated: true` header
 */
export async function apiSecureFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
  token?: string | null,
): Promise<T> {
  const { cacheAuth, ...fetchOptions } = options;
  const method = (fetchOptions.method ?? "GET").toUpperCase();
  const headers = new Headers(fetchOptions.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && fetchOptions.body) {
    headers.set("Content-Type", "application/json");
  }
  const activeOrgId = getActiveOrgId();
  if (activeOrgId) {
    headers.set("X-Active-Org-Id", activeOrgId);
  }

  // Normalize path to match backend's request.originalUrl format (with leading slash)
  // Also strip query string since backend validates against path only
  const pathWithSlash = path.startsWith("/") ? path : `/${path}`;
  const normalizedPath = pathWithSlash.split('?')[0];

  // Prepare body for signing and obfuscation
  // IMPORTANT: We must use the SAME string for both signing and obfuscation
  // obfuscatePayload internally calls JSON.stringify, so we need to match that exactly
  let bodyStringForSigning: string | undefined;
  let processedBody: BodyInit | null | undefined = fetchOptions.body;

  if (fetchOptions.body) {
    // Parse to object first (if string), then re-stringify to get canonical form
    // This ensures we sign the same string that obfuscatePayload will use
    const bodyObj =
      typeof fetchOptions.body === "string"
        ? JSON.parse(fetchOptions.body)
        : fetchOptions.body;
    // This is the exact string that will be XOR'd and sent to backend
    bodyStringForSigning = JSON.stringify(bodyObj);
    processedBody = obfuscatePayload(bodyObj);
  }

  // Add security headers (include body in signature)
  const securityHeaders = await createSecureHeaders(normalizedPath, bodyStringForSigning);
  Object.entries(securityHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  // Mark request as obfuscated and set Content-Type to text/plain
  // so NestJS doesn't try to parse the obfuscated string as JSON
  // Only set these headers when there's actually a body to obfuscate
  if (processedBody) {
    headers.set("x-obfuscated", "true");
    headers.set("Content-Type", "text/plain");
  }

  const url = buildApiUrl(path);
  const hasAuth = headers.has("Authorization");
  const cacheMode = fetchOptions.cache;
  const canCache =
    isClient &&
    method === "GET" &&
    !fetchOptions.body &&
    !shouldBypassClientCache(path) &&
    cacheMode !== "no-store" &&
    (!hasAuth || cacheAuth);
  const cacheKey = `${envPrefix}:secure:${method}:${url}`;

  if (canCache) {
    const cached = getCachedValue<T>(cacheKey);
    if (cached !== null) {
      return cached;
    }
    const pending = inFlight.get(cacheKey);
    if (pending) {
      return (await pending) as T;
    }
  }

  const requestPromise = (async () => {
    const fetchController = new AbortController();
    const fetchTimeout = setTimeout(() => fetchController.abort(), 40000);

    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchOptions,
        body: processedBody,
        headers,
        signal: fetchOptions.signal ?? fetchController.signal,
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      throw err;
    } finally {
      clearTimeout(fetchTimeout);
    }

    if (!response.ok) {
      // Read key derivation components in case the error body is obfuscated.
      const xorUa = response.headers.get("x-xor-ua");
      const xorCd = response.headers.get("x-xor-cd");
      const text = await response.text();
      const fallbackMessage = `Request failed (${response.status})`;
      let message = text || fallbackMessage;

      try {
        const parsed = JSON.parse(text);
        if (
          parsed &&
          typeof parsed === "object" &&
          typeof (parsed as Record<string, unknown>).data === "string" &&
          ((parsed as Record<string, unknown>).data as string).startsWith("v1:")
        ) {
          const decoded = tryDeobfuscate<Record<string, unknown>>(
            (parsed as Record<string, unknown>).data as string,
            xorUa,
            xorCd,
          );
          message = extractErrorMessage(decoded, fallbackMessage);
        } else {
          message = extractErrorMessage(parsed, fallbackMessage);
        }
      } catch {
        if (text.startsWith("v1:")) {
          try {
            const decoded = tryDeobfuscate<Record<string, unknown>>(text, xorUa, xorCd);
            message = extractErrorMessage(decoded, fallbackMessage);
          } catch {
            message = fallbackMessage;
          }
        }
      }

      if (response.status === 401) {
        throw new AuthExpiredError();
      }

      throw new Error(message);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    // Read key derivation components sent by backend interceptor
    // These guarantee the frontend uses the exact same XOR key as the backend
    const xorUa = response.headers.get("x-xor-ua");
    const xorCd = response.headers.get("x-xor-cd");

    // Check if response is obfuscated and deobfuscate
    const isObfuscated = response.headers.get("x-obfuscated") === "true";
    if (isObfuscated) {
      // Backend returns { data: "v1:...obfuscated..." }
      const responseJson = await response.json();
      const obfuscatedText = responseJson.data || responseJson;
      const text =
        typeof obfuscatedText === "string"
          ? obfuscatedText
          : JSON.stringify(obfuscatedText);
      return tryDeobfuscate<T>(text, xorUa, xorCd);
    }

    const result = await response.json();

    // Fallback: the x-obfuscated header may be stripped by the proxy layer,
    // so detect obfuscated payloads by their shape: { data: "v1:..." }
    if (
      result &&
      typeof result === "object" &&
      typeof result.data === "string" &&
      result.data.startsWith("v1:")
    ) {
      return tryDeobfuscate<T>(result.data, xorUa, xorCd);
    }

    return result as T;
  })();

  if (canCache) {
    inFlight.set(cacheKey, requestPromise as Promise<unknown>);
  }

  try {
    const data = await requestPromise;
    if (canCache) {
      setCachedValue(cacheKey, data, DEFAULT_CACHE_TTL_MS);
    }
    return data;
  } finally {
    if (canCache) {
      inFlight.delete(cacheKey);
    }
  }
}

/**
 * Secure version of apiFetchAuthed that adds security headers and body obfuscation.
 * Combines authentication with security features.
 */
export async function apiSecureFetchAuthed<T>(
  path: string,
  options: ApiFetchOptions = {},
  getToken: TokenProvider,
): Promise<T> {
  const token = await getToken();
  return apiSecureFetch<T>(path, options, token);
}

export async function apiFetchAuthed<T>(
  path: string,
  options: ApiFetchOptions = {},
  getToken: TokenProvider,
): Promise<T> {
  // getToken() calls Clerk's token refresh endpoint — retry once on transient TypeError
  let token: string | null;
  try {
    token = await getToken();
  } catch (err) {
    if (err instanceof TypeError) {
      try {
        token = await getToken();
      } catch (retryErr) {
        throw new Error(`auth: ${retryErr instanceof Error ? retryErr.message : String(retryErr)}`);
      }
    } else {
      throw new Error(`auth: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // Automatically use secure fetch for non-public paths
  if (!isInsecurePath(path)) {
    return apiSecureFetch<T>(path, options, token);
  }

  return apiFetch<T>(path, options, token);
}
